/**
 * Helper functions for subscription data normalization and processing
 */

import type {
  Plan,
  AssignedPlanResponse,
  SubscriptionPayment,
  PaginatedResponse,
  PaginationInfo,
  PaymentFilters,
  PaymentStatus
} from '@/types/subscription'

/**
 * Normalize plans response from API to consistent Plan[] format
 * Handles different response structures:
 * - Direct Plan object
 * - AssignedPlanResponse with has_plan flag
 */
export const normalizePlansResponse = (rawData: any): Plan[] => {
  if (!rawData) {
    return []
  }

  // Check if plan is nested in a 'plan' property
  if (rawData.plan && typeof rawData.plan === 'object') {
    return [rawData.plan as Plan]
  }

  // Single plan object (direct plan from API)
  if (rawData.id || rawData.public_id) {
    return [rawData as Plan]
  }

  // Assigned plan response (flattened structure with plan_ prefix)
  if (Object.prototype.hasOwnProperty.call(rawData, 'has_plan')) {
    const assignedPlanData = rawData as AssignedPlanResponse

    if (!assignedPlanData.has_plan) {
      return []
    }

    return [
      {
        id: assignedPlanData.plan_id,
        public_id: assignedPlanData.plan_public_id,
        name: assignedPlanData.plan_name,
        description: assignedPlanData.plan_description,
        stripe_price_id: assignedPlanData.stripe_price_id,
        amount: assignedPlanData.amount,
        currency: assignedPlanData.currency,
        interval: assignedPlanData.interval,
        is_active: assignedPlanData.is_active,
        formatted_amount: assignedPlanData.formatted_amount
      }
    ]
  }

  return []
}

/**
 * Type guard to check if response is paginated payments response
 */
export const isPaginatedPaymentsResponse = (
  data: any
): data is PaginatedResponse<SubscriptionPayment> => {
  return data && typeof data === 'object' && 'results' in data
}

/**
 * Extract payments array from response
 * Handles both array and paginated response formats
 */
export const extractPaymentsArray = (rawData: any): SubscriptionPayment[] => {
  if (Array.isArray(rawData)) {
    return rawData
  }

  if (isPaginatedPaymentsResponse(rawData)) {
    return rawData.results
  }

  return []
}

/**
 * Create pagination info from array of items
 */
export const createPaginationFromArray = (
  allItems: SubscriptionPayment[],
  filters: PaymentFilters
): PaginationInfo | null => {
  if (!allItems.length) {
    return null
  }

  const pageSize = filters.page_size || 10
  const currentPage = filters.page || 1
  const totalPages = Math.ceil(allItems.length / pageSize)

  return {
    current_page: currentPage,
    total_pages: totalPages,
    page_size: pageSize,
    has_next: currentPage < totalPages,
    has_previous: currentPage > 1
  }
}

/**
 * Paginate array data based on filters
 */
export const paginateArrayData = (
  allItems: SubscriptionPayment[],
  filters: PaymentFilters
): SubscriptionPayment[] => {
  const pageSize = filters.page_size || 10
  const currentPage = filters.page || 1
  const startIndex = (currentPage - 1) * pageSize
  const endIndex = startIndex + pageSize

  return allItems.slice(startIndex, endIndex)
}

/**
 * Create pagination info from total count
 */
export const createPaginationInfo = (
  totalCount: number,
  filters: PaymentFilters
): PaginationInfo => {
  const pageSize = filters.page_size || 10
  const currentPage = filters.page || 1
  const totalPages = Math.ceil(totalCount / pageSize)

  return {
    current_page: currentPage,
    total_pages: totalPages,
    page_size: pageSize,
    has_next: currentPage < totalPages,
    has_previous: currentPage > 1
  }
}

/**
 * Calculate status counts from payments array
 */
export const calculateStatusCounts = (
  payments: SubscriptionPayment[]
): Record<PaymentStatus, number> => {
  return payments.reduce(
    (acc: Record<PaymentStatus, number>, payment: SubscriptionPayment) => {
      acc[payment.status] = (acc[payment.status] || 0) + 1
      return acc
    },
    {} as Record<PaymentStatus, number>
  )
}

/**
 * Check if there's a next page in paginated response
 */
export const hasNextPage = (
  responseData: any,
  currentPage: number,
  pageSize: number
): boolean => {
  if (responseData.page_info) {
    return responseData.page_info.has_next
  }

  // Calculate if there are more pages based on total count
  const totalPages = Math.ceil(responseData.count / pageSize)
  return currentPage < totalPages
}
